import startCase from "lodash/startCase";
import toLower from "lodash/toLower";
import upperFirst from "lodash/upperFirst";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import type { insuranceCoverageApiSpec } from "@wingspanhq/wingspan-api-types";
import { DataItemProps } from "../../../../../../shared/components/DataView";
import { selectorPolicyEffectiveDatesText } from "../../../../selectors/selectorPolicyEffectiveDatesText";

const sentenceCase = (str: string) => upperFirst(toLower(startCase(str)));

export const mapInsuranceCoverageToDataView = (
  coverage: insuranceCoverageApiSpec.InsuranceCoverage,
  {
    showBasicInfo = true,
    showLimits = true
  }: { showBasicInfo?: boolean; showLimits?: boolean } = {}
): DataItemProps[] => {
  const items: DataItemProps[] = !showBasicInfo
    ? []
    : [
        {
          label: "Policy type",
          value: sentenceCase(coverage.policyType)
        },
        { label: "Policy number", value: coverage.policyNumber },
        { label: "Insurer name", value: coverage.insurerName },
        {
          label: "Policy effective dates",
          fullWidth: false,
          value: selectorPolicyEffectiveDatesText(coverage)
        }
      ];

  if (showLimits && coverage.limits?.length) {
    items.push({
      fullWidth: true,
      label: `Limits`,
      value: coverage.limits
        .map((limit, index) => {
          // TODO(artemk): update api interface
          return `• ${sentenceCase(limit.limitType)}: ${formatMoney(
            limit.amount,
            limit.currency as Currency
          )} ${limit.currency ? `(${limit.currency})` : ""}`;
        })
        .join("\n")
    });
  }

  if (coverage.appliedProvisions?.length) {
    items.push({
      label: "Applied provisions",
      fullWidth: false,
      value: coverage.appliedProvisions
        .map((provision, index) => {
          const provisionType = sentenceCase(provision.provisionType || "");
          const condition = provision.condition
            ? `(${provision.condition})`
            : "";

          const basis = sentenceCase(provision.basis || "");
          const appliesTo = provision.appliesTo
            ? `Applies To: ${sentenceCase(provision.appliesTo)}`
            : "";

          const firstPart = [provisionType, condition]
            .filter(Boolean)
            .join(" ");
          const secondPart = [basis, appliesTo].filter(Boolean).join(" - ");

          return "• " + [firstPart, secondPart].filter(Boolean).join(": ");
        })
        .join("\n")
    });
  }

  if (coverage.issuingOrganization) {
    items.push({
      label: "Issuing organization",
      value: coverage.issuingOrganization
    });
  }

  if (coverage.coverageForm) {
    items.push({
      label: "Coverage form",
      value: sentenceCase(coverage.coverageForm)
    });
  }

  if (coverage.certificateHolder) {
    const parts: string[] = [];
    if (coverage.certificateHolder.name) {
      parts.push(coverage.certificateHolder.name);
    }
    if (coverage.certificateHolder.address) {
      parts.push(coverage.certificateHolder.address);
    }

    if (parts.length) {
      items.push({
        label: "Certificate holder",
        fullWidth: false,
        value: parts.join("\n")
      });
    }
  }

  if (coverage.namedInsured) {
    const parts: string[] = [];
    if (coverage.namedInsured.name) {
      parts.push(coverage.namedInsured.name);
    }
    if (coverage.namedInsured.address) {
      parts.push(coverage.namedInsured.address);
    }

    if (parts.length) {
      items.push({
        label: "Named insured",
        fullWidth: false,
        value: parts.join("\n")
      });
    }
  }

  if (coverage.territorialLimits) {
    items.push({
      label: "Territorial limits",
      value: coverage.territorialLimits
    });
  }

  if (coverage.descriptionOfOperations) {
    items.push({
      label: "Description of operations",
      value: coverage.descriptionOfOperations
    });
  }

  if (coverage.cancellationNotice) {
    const notice = coverage.cancellationNotice;
    const noticeParts: string[] = [];

    if (notice.noticePeriodDays != null) {
      noticeParts.push(`${notice.noticePeriodDays} days notice`);
    }

    if (notice.noticeRecipient) {
      noticeParts.push(`Recipient: ${sentenceCase(notice.noticeRecipient)}`);
    }

    if (notice.rawText) {
      noticeParts.push(`Details: ${notice.rawText}`);
    }

    if (noticeParts.length) {
      items.push({
        label: "Cancellation notice",
        fullWidth: true,
        value: noticeParts.join("\n")
      });
    }
  }

  if (coverage.policySpecificAttributes?.auto) {
    const auto = coverage.policySpecificAttributes.auto;
    const autoParts: string[] = [];

    if (auto.autoCoverageKinds?.length) {
      autoParts.push(
        `Coverage Kinds: ${auto.autoCoverageKinds
          .map(kind => sentenceCase(kind))
          .join(", ")}`
      );
    }

    if (auto.coveredAutoTypes?.length) {
      autoParts.push(
        `Covered Auto Types: ${auto.coveredAutoTypes
          .map(type => sentenceCase(type))
          .join(", ")}`
      );
    }

    if (auto.hnoaEvidence != null) {
      autoParts.push(
        `Hired & Non-Owned Auto Evidence: ${auto.hnoaEvidence ? "Yes" : "No"}`
      );
    }

    if (autoParts.length) {
      items.push({
        label: "Auto policy details",
        fullWidth: true,
        value: autoParts.join("\n")
      });
    }
  }

  return items;
};
